import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import { ChevronDown } from "lucide-react";
import Nav from "./Nav";
import { CompassFace, Star } from "./Compass";
import { MaskLine } from "./Reveal";

const CATEGORIES = ["CLOTHING", "BAGS", "JEWELRY", "CUSTOMS"];

export default function Hero({ onCheckout }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const auraY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      data-testid="hero"
      onMouseMove={onMove}
      className="grain relative min-h-screen overflow-hidden"
    >
      <motion.div style={{ y: auraY }} className="aura-hero absolute -inset-[12%]" />
      <Nav onCheckout={onCheckout} />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex min-h-screen items-center justify-center px-6"
      >
        <motion.div
          style={{ rotateX, rotateY, transformPerspective: 1100 }}
          className="relative flex items-center justify-center"
        >
          <div className="aura-strong absolute aspect-square w-[min(70vw,480px)] rounded-full" />
          <div className="spin-slower">
            <CompassFace showText={false} className="aspect-square w-[min(88vw,620px)]" />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <MaskLine delay={0.25}>
              <img
                src="/assets/logo-mark.png"
                alt="MarStar Originals"
                data-testid="hero-logo"
                className="w-[clamp(300px,42vw,560px)]"
                style={{ filter: "drop-shadow(0 6px 44px rgba(43,47,217,0.5))" }}
              />
            </MaskLine>
          </div>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute inset-0 z-10 hidden md:block">
        <div className="absolute left-12 top-1/2 flex -translate-y-1/2 flex-col gap-4">
          {CATEGORIES.map((c, i) => (
            <MaskLine key={c} delay={0.7 + i * 0.1}>
              <a
                href="#shop"
                data-testid={`hero-category-${c.toLowerCase()}`}
                className="block font-mono text-[11px] tracking-[0.35em] text-cream/80 transition-all hover:translate-x-2 hover:text-white"
              >
                {c}
              </a>
            </MaskLine>
          ))}
        </div>
        <div className="absolute right-12 top-1/2 -translate-y-1/2 text-right font-mono text-[11px] leading-7 tracking-[0.35em] text-cream/85">
          <MaskLine delay={0.8}>HANDMADE</MaskLine>
          <MaskLine delay={0.9}>ORIGINAL</MaskLine>
          <MaskLine delay={1.0}>FOR THE SUN</MaskLine>
          <MaskLine delay={1.1}>AND BEYOND</MaskLine>
          <MaskLine delay={1.25}>
            <span className="mt-3 flex justify-end">
              <Star x="0" y="0" s="9" />
            </span>
          </MaskLine>
        </div>
        <div className="absolute inset-x-0 bottom-9 flex flex-col items-center gap-2">
          <MaskLine delay={1.3}>
            <span className="font-mono text-[11px] tracking-[0.5em] text-cream/85">EXPLORE</span>
          </MaskLine>
          <ChevronDown size={16} className="drift text-cream/80" />
        </div>
      </motion.div>
    </section>
  );
}
